import React, {useCallback, useEffect, useRef, useState} from 'react';
import createAxiosInstance from '../support/AxiosSupport';
import {ChevronDownIcon, SearchIcon} from './icons';

interface UserOption {
    id: string;
    username: string | null;
    name: string | null;
}

interface UserSearchSelectProps {
    value: string;
    onChange: (userId: string, user?: UserOption) => void;
    placeholder?: string;
    className?: string;
    minSearchLength?: number;
}

const UserSearchSelect: React.FC<UserSearchSelectProps> = ({
    value,
    onChange,
    placeholder = 'Поиск по имени или логину',
    className = '',
    minSearchLength = 2
}) => {
    const [query, setQuery] = useState('');
    const [open, setOpen] = useState(false);
    const [results, setResults] = useState<UserOption[]>([]);
    const [loading, setLoading] = useState(false);
    const [selectedUser, setSelectedUser] = useState<UserOption | null>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const debounceRef = useRef<ReturnType<typeof setTimeout>>();

    const displayValue = open ? query : (selectedUser ? (selectedUser.name || selectedUser.username || selectedUser.id) : value || '');

    const searchUsers = useCallback(async (q: string) => {
        if (!q || q.length < minSearchLength) {
            setResults([]);
            return;
        }
        setLoading(true);
        try {
            const api = await createAxiosInstance();
            const res = await api.get<{ content: UserOption[] }>('/users/search', {params: {q, page: 0, size: 15}});
            setResults(res.data?.content ?? []);
        } catch (err) {
            console.error('Error searching users:', err);
            setResults([]);
        } finally {
            setLoading(false);
        }
    }, [minSearchLength]);

    useEffect(() => {
        if (debounceRef.current) clearTimeout(debounceRef.current);
        if (!query.trim()) {
            setResults([]);
            return;
        }
        debounceRef.current = setTimeout(() => searchUsers(query.trim()), 300);
        return () => {
            if (debounceRef.current) clearTimeout(debounceRef.current);
        };
    }, [query, searchUsers]);

    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
                setOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleSelect = useCallback((u: UserOption) => {
        setSelectedUser(u);
        onChange(u.id, u);
        setQuery('');
        setOpen(false);
        setResults([]);
    }, [onChange]);

    const handleClear = useCallback(() => {
        setSelectedUser(null);
        onChange('', undefined);
        setQuery('');
        setOpen(false);
        setResults([]);
    }, [onChange]);

    const formatUser = (u: UserOption) => {
        const parts: string[] = [];
        if (u.name) parts.push(u.name);
        if (u.username && u.username !== u.name) parts.push(`@${u.username}`);
        return parts.length > 0 ? parts.join(' • ') : u.id;
    };

    return (
        <div ref={containerRef} className={`relative ${className}`}>
            <div className="flex gap-1">
                <div className="relative flex-1">
                    <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 dark:text-gray-500"/>
                    <input
                        type="text"
                        value={displayValue}
                        onChange={e => {
                            setQuery(e.target.value);
                            setOpen(true);
                            if (!e.target.value) setSelectedUser(null);
                        }}
                        onFocus={() => setOpen(true)}
                        placeholder={placeholder}
                        className="input-field w-full pl-9"
                    />
                </div>
                <button
                    type="button"
                    onClick={() => setOpen(!open)}
                    className="px-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-700"
                >
                    <ChevronDownIcon className={`w-5 h-5 transition-transform ${open ? 'rotate-180' : ''}`}/>
                </button>
            </div>
            {open && (
                <div className="absolute z-20 mt-1 w-full bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg shadow-lg max-h-56 overflow-y-auto">
                    {query.trim().length < minSearchLength && (
                        <div className="px-3 py-3 text-sm text-gray-500 dark:text-gray-400">
                            Введите не менее {minSearchLength} символов для поиска
                        </div>
                    )}
                    {loading && (
                        <div className="px-3 py-3 text-sm text-gray-500 dark:text-gray-400">Поиск...</div>
                    )}
                    {!loading && query.trim().length >= minSearchLength && results.length === 0 && (
                        <div className="px-3 py-3 text-sm text-gray-500 dark:text-gray-400">
                            Ничего не найдено. Пользователи появляются после первого входа.
                        </div>
                    )}
                    {!loading && results.map(u => (
                        <button
                            key={u.id}
                            type="button"
                            onClick={() => handleSelect(u)}
                            className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-700 ${value === u.id ? 'bg-primary-50 dark:bg-primary-900/30' : ''}`}
                        >
                            <div className="font-medium text-gray-900 dark:text-gray-100">{formatUser(u)}</div>
                            {u.id && (
                                <div className="text-xs text-gray-500 dark:text-gray-400 font-mono truncate">{u.id}</div>
                            )}
                        </button>
                    ))}
                    {(value || selectedUser) && (
                        <button
                            type="button"
                            onClick={handleClear}
                            className="w-full text-left px-3 py-2 text-sm text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700 border-t border-gray-200 dark:border-gray-600"
                        >
                            Очистить
                        </button>
                    )}
                </div>
            )}
        </div>
    );
};

export default UserSearchSelect;
export type {UserOption};
